import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Flex, Stack } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Alert,
  Button,
  Card,
  EmptyState,
  Filters,
  LoadingState,
  Search,
  SectionHeader,
  Select,
  StatusChip,
  Table,
  Tabs,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { ApiError, apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createTasksApi } from "../api";
import {
  dueLabel,
  formatLabel,
  isClosedStatus,
  priorityTone,
  SOURCE_LABEL,
  taskStatusTone,
} from "../labels";
import type { BulkTaskAction, ListParams, Me, TaskAction, TaskDto, TaskSummary } from "../types";
import { CreateTaskDialog, DEPARTMENTS, PRIORITIES, TaskActionDialog } from "../components/TaskDialogs";

type View = "mine" | "unassigned" | "overdue" | "open" | "done";

const VIEWS: { id: View; label: string; empty: string }[] = [
  { id: "mine", label: "Mine", empty: "Nothing is assigned to you. Pick something up from Unassigned." },
  { id: "unassigned", label: "Unassigned", empty: "Every open task has an owner." },
  { id: "overdue", label: "Overdue", empty: "Nothing is past its due date." },
  { id: "open", label: "All open", empty: "No open work. New tickets, reports, refunds, applications and approvals appear here on their own." },
  { id: "done", label: "Done", empty: "No finished tasks yet." },
];

const PAGE = 25;

function viewParams(view: View): ListParams {
  switch (view) {
    case "mine":
      return { assignee: "me", open: true };
    case "unassigned":
      return { assignee: "unassigned", open: true };
    case "overdue":
      return { overdue: true };
    case "done":
      return { status: "completed,cancelled" };
    default:
      return { open: true };
  }
}

/** The Task Center: one work queue, the home page and the /queue route alike. */
function TaskCenter() {
  const navigate = useNavigate();
  const toast = useToast();
  const client = useApiClient();
  const api = useMemo(() => createTasksApi(client), [client]);
  const [params, setParams] = useSearchParams();

  const view = (params.get("view") as View | null) ?? "mine";
  const q = params.get("q") ?? "";
  const priority = params.get("priority") ?? "";
  const dept = params.get("owning_department") ?? "";
  const origin = params.get("origin") ?? "";
  const sort = params.get("sort") ?? "due_at";
  const sourceType = params.get("source_type") ?? "";
  const sourceId = params.get("source_id") ?? "";
  const statusFilter = params.get("status") ?? "";

  const [search, setSearch] = useState(q);
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [rows, setRows] = useState<TaskDto[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [next, setNext] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [summary, setSummary] = useState<TaskSummary | null>(null);
  const [me, setMe] = useState<Me | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkAction, setBulkAction] = useState<BulkTaskAction | null>(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const cursor = cursors[cursors.length - 1];

  function update(patch: Record<string, string>) {
    const nextParams = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) nextParams.set(k, v);
      else nextParams.delete(k);
    }
    setParams(nextParams);
    setCursors([undefined]);
    setSelected(new Set());
  }

  // Debounced search into the URL.
  useEffect(() => {
    const t = setTimeout(() => {
      if (search !== q) update({ q: search.trim() });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [list, sum] = await Promise.all([
        api.listTasks({
          ...viewParams(view),
          limit: PAGE,
          sort: view === "done" ? "newest" : sort,
          ...(cursor ? { cursor } : {}),
          ...(statusFilter ? { status: statusFilter } : {}),
          ...(q ? { q } : {}),
          ...(priority ? { priority } : {}),
          ...(dept ? { owning_department: dept } : {}),
          ...(origin === "manual" || origin === "system" ? { origin } : {}),
          ...(sourceType ? { source_type: sourceType } : {}),
          ...(sourceId ? { source_id: sourceId } : {}),
        }),
        api.summary(),
      ]);
      setRows(list.items);
      setNext(list.next_cursor ?? null);
      setTotal(list.meta.total ?? null);
      setSummary(sum);
      setForbidden(false);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setForbidden(true);
      setError(apiErrorMessage(err));
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [api, view, sort, cursor, q, priority, dept, origin, sourceType, sourceId, statusFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    api.me().then(setMe).catch(() => setMe(null));
  }, [api]);

  const counts: Record<View, number | null> = {
    mine: summary?.mine ?? null,
    unassigned: summary?.unassigned ?? null,
    overdue: summary?.overdue ?? null,
    open: summary?.open ?? null,
    done: null,
  };

  const allSelected = rows.length > 0 && rows.every((r) => selected.has(r.id));

  async function runBulk(body: { owner_user_id?: string; reason?: string; outcome?: string }) {
    if (!bulkAction) return;
    setBulkBusy(true);
    setBulkError(null);
    try {
      const r = await api.bulkAction({ task_ids: [...selected], action: bulkAction, ...body });
      const failures = r.results.filter((x) => !x.ok);
      toast.push({
        title: `${r.succeeded} updated${r.failed ? `, ${r.failed} skipped` : ""}`,
        ...(failures.length ? { description: [...new Set(failures.map((f) => f.error))].join("; ") } : {}),
        tone: r.failed ? "warning" : "success",
      });
      setBulkAction(null);
      setSelected(new Set());
      void load();
    } catch (err) {
      setBulkError(apiErrorMessage(err));
    } finally {
      setBulkBusy(false);
    }
  }

  async function assignToMe() {
    if (!me) return;
    setBulkAction("assign");
    setBulkBusy(true);
    try {
      const r = await api.bulkAction({ task_ids: [...selected], action: "assign", owner_user_id: me.user_id });
      toast.push({ title: `${r.succeeded} assigned to you${r.failed ? `, ${r.failed} skipped (already assigned or closed)` : ""}`, tone: r.failed ? "warning" : "success" });
      setSelected(new Set());
      void load();
    } catch (err) {
      toast.push({ title: "Could not assign", description: apiErrorMessage(err), tone: "danger" });
    } finally {
      setBulkBusy(false);
      setBulkAction(null);
    }
  }

  const table = (
    <Stack gap={12}>
      {selected.size > 0 ? (
        <ActionBar
          left={<Text role="label" as="span">{selected.size} selected</Text>}
          right={
            <Flex gap={8} wrap>
              <Button size="sm" onClick={() => void assignToMe()} disabled={bulkBusy || !me}>
                Assign to me
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setBulkAction("assign")}>
                Assign to…
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setBulkAction("start")}>
                Start
              </Button>
              <Button size="sm" variant="secondary" onClick={() => setBulkAction("complete")}>
                Complete
              </Button>
              <Button size="sm" variant="danger" onClick={() => setBulkAction("cancel")}>
                Cancel
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>
                Clear
              </Button>
            </Flex>
          }
        />
      ) : null}
      {loading ? <LoadingState message="Loading tasks…" /> : null}
      {!loading && error && !forbidden ? (
        <Alert tone="danger" title="Could not load tasks">
          {error}{" "}
          <Button size="sm" variant="ghost" onClick={() => void load()}>
            Retry
          </Button>
        </Alert>
      ) : null}
      {!loading && !error && rows.length === 0 ? (
        <EmptyState
          title={q || priority || dept || origin || statusFilter ? "No tasks match these filters" : "Nothing here"}
          description={
            q || priority || dept || origin || statusFilter
              ? "Clear the filters to see everything in this view."
              : VIEWS.find((v) => v.id === view)?.empty
          }
          action={
            q || priority || dept || origin || statusFilter ? (
              <Button variant="secondary" onClick={() => { setSearch(""); update({ q: "", priority: "", owning_department: "", origin: "", status: "" }); }}>
                Clear filters
              </Button>
            ) : (
              <Button onClick={() => setCreateOpen(true)}>New task</Button>
            )
          }
        />
      ) : null}
      {!loading && rows.length > 0 ? (
        <Table>
          <Thead>
            <Tr>
              <Th>
                <input
                  type="checkbox"
                  aria-label="Select all on this page"
                  checked={allSelected}
                  onChange={() =>
                    setSelected(allSelected ? new Set() : new Set(rows.map((r) => r.id)))
                  }
                />
              </Th>
              <Th>Task</Th>
              <Th>Priority</Th>
              <Th>Status</Th>
              <Th>Owner</Th>
              <Th>Due</Th>
              <Th>Team</Th>
            </Tr>
          </Thead>
          <Tbody>
            {rows.map((row) => {
              const closed = isClosedStatus(row.status);
              return (
                <Tr key={row.id} style={{ cursor: "pointer" }} onClick={() => void navigate(`/app/tasks/${row.id}`)}>
                  <Td onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${row.title ?? row.id}`}
                      checked={selected.has(row.id)}
                      onChange={() => {
                        const s = new Set(selected);
                        if (s.has(row.id)) s.delete(row.id);
                        else s.add(row.id);
                        setSelected(s);
                      }}
                    />
                  </Td>
                  <Td>
                    <Stack gap={4}>
                      <Text role="label" as="span">
                        {row.title ?? "Untitled task"}
                      </Text>
                      <Flex gap={8} wrap>
                        {row.source_type ? (
                          <Text role="caption" color="secondary" as="span">
                            {SOURCE_LABEL[row.source_type] ?? formatLabel(row.source_type)}
                          </Text>
                        ) : null}
                        {row.origin === "system" ? (
                          <Text role="caption" color="muted" as="span">
                            · opened automatically
                          </Text>
                        ) : row.created_by_name ? (
                          <Text role="caption" color="muted" as="span">
                            · by {row.created_by_name}
                          </Text>
                        ) : null}
                      </Flex>
                    </Stack>
                  </Td>
                  <Td>
                    <StatusChip tone={priorityTone(row.priority)}>{formatLabel(row.priority)}</StatusChip>
                  </Td>
                  <Td>
                    <StatusChip tone={taskStatusTone(row.status)}>{formatLabel(row.status)}</StatusChip>
                  </Td>
                  <Td>{row.owner_user_id ? (row.owner_user_id === me?.user_id ? "Me" : (row.owner_name ?? "Staff member")) : <Text role="small" color="muted" as="span">Unassigned</Text>}</Td>
                  <Td>
                    <Text role="small" as="span" style={row.is_overdue ? { color: "var(--color-semantic-danger, #c62828)", fontWeight: 600 } : undefined}>
                      {dueLabel(row.due_at, closed)}
                    </Text>
                  </Td>
                  <Td>{formatLabel(row.owning_department)}</Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      ) : null}
      {!loading && rows.length > 0 ? (
        <ActionBar
          left={
            <Text role="small" color="secondary" as="span">
              {total !== null
                ? `${(cursors.length - 1) * PAGE + 1}–${(cursors.length - 1) * PAGE + rows.length} of ${total}`
                : `Page ${cursors.length}`}
            </Text>
          }
          right={
            <Flex gap={8}>
              <Button size="sm" variant="secondary" disabled={cursors.length <= 1} onClick={() => setCursors((c) => c.slice(0, -1))}>
                Previous
              </Button>
              <Button size="sm" variant="secondary" disabled={!next} onClick={() => next && setCursors((c) => [...c, next])}>
                Next
              </Button>
            </Flex>
          }
        />
      ) : null}
    </Stack>
  );

  if (forbidden) {
    return (
      <Stack gap={24}>
        <SectionHeader title="Task Center" />
        <EmptyState
          title="You don't have access to the Task Center"
          description="The Task Center needs the Operations › View permission (and Operations › Manage to work tasks). A Super Admin can grant it under System › Access."
          action={<Button variant="secondary" onClick={() => void navigate("/app/system/access")}>Open Access</Button>}
        />
      </Stack>
    );
  }

  return (
    <Stack gap={24}>
      <SectionHeader
        title="Task Center"
        description="Everything someone at HQ has to do. Tickets, reports, refunds, payouts, disputes, business and creator applications, document reviews and approvals open a task on their own and close it when they're handled."
        actions={
          <Flex gap={8} wrap>
            <Button onClick={() => setCreateOpen(true)}>New task</Button>
            <Button variant="secondary" onClick={() => void load()}>
              Refresh
            </Button>
          </Flex>
        }
      />

      {summary ? (
        <Flex gap={12} wrap>
          {(
            [
              ["Assigned to me", summary.mine, "mine"],
              ["Unassigned", summary.unassigned, "unassigned"],
              ["Overdue", summary.overdue, "overdue"],
              ["Blocked", summary.blocked, null],
              ["Urgent or critical", summary.urgent, null],
            ] as [string, number, View | null][]
          ).map(([label, n, target]) => (
            <Card
              key={label}
              style={{ minWidth: 150, cursor: "pointer" }}
              onClick={() => {
                if (target) update({ view: target, status: "", priority: "" });
                else if (label === "Blocked") update({ view: "open", status: "blocked", priority: "" });
                else update({ view: "open", status: "", priority: "urgent,critical" });
              }}
            >
              <Stack gap={4}>
                <Text role="caption" color="secondary" as="span">{label}</Text>
                <Text role="h2" as="span">{n}</Text>
              </Stack>
            </Card>
          ))}
        </Flex>
      ) : null}

      {sourceType && sourceId ? (
        <Alert tone="info" title="Filtered to one record">
          Showing tasks for this {SOURCE_LABEL[sourceType] ?? formatLabel(sourceType)} only.{" "}
          <Button size="sm" variant="ghost" onClick={() => update({ source_type: "", source_id: "" })}>
            Show all
          </Button>
        </Alert>
      ) : null}

      <Filters>
        <Flex gap={12} wrap align="end">
          <Search value={search} onValueChange={setSearch} placeholder="Search task titles and details" label="Search tasks" />
          <Select
            label="Priority"
            value={priority}
            onChange={(e) => update({ priority: e.target.value })}
            options={[
              { value: "", label: "Any priority" },
              { value: "urgent,critical", label: "Urgent or critical" },
              ...PRIORITIES.map((p) => ({ value: p, label: formatLabel(p) })),
            ]}
          />
          <Select
            label="Team"
            value={dept}
            onChange={(e) => update({ owning_department: e.target.value })}
            options={[{ value: "", label: "All teams" }, ...DEPARTMENTS.map((d) => ({ value: d, label: `${formatLabel(d)}${summary?.by_department[d] ? ` (${summary.by_department[d]})` : ""}` }))]}
          />
          <Select
            label="Origin"
            value={origin}
            onChange={(e) => update({ origin: e.target.value })}
            options={[
              { value: "", label: "Any" },
              { value: "system", label: "Opened automatically" },
              { value: "manual", label: "Created by a person" },
            ]}
          />
          <Select
            label="Sort"
            value={sort}
            onChange={(e) => update({ sort: e.target.value })}
            options={[
              { value: "due_at", label: "Needs attention first" },
              { value: "priority", label: "Priority" },
              { value: "newest", label: "Newest" },
              { value: "oldest", label: "Oldest" },
            ]}
          />
        </Flex>
      </Filters>

      <Tabs
        value={view}
        onValueChange={(id) => update({ view: id, status: "" })}
        items={VIEWS.map((v) => ({
          id: v.id,
          label: counts[v.id] !== null ? `${v.label} (${counts[v.id]})` : v.label,
          content: table,
        }))}
      />

      <CreateTaskDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        busy={createBusy}
        error={createError}
        meId={me?.user_id}
        onSubmit={async (body) => {
          setCreateBusy(true);
          setCreateError(null);
          try {
            const result = await api.createTask(body);
            toast.push({ title: "Task created", tone: "success" });
            setCreateOpen(false);
            void navigate(`/app/tasks/${result.task.id}`);
          } catch (err) {
            setCreateError(apiErrorMessage(err));
          } finally {
            setCreateBusy(false);
          }
        }}
      />

      <TaskActionDialog
        open={bulkAction !== null && !(bulkAction === "assign" && bulkBusy && !bulkError)}
        action={bulkAction as TaskAction | null}
        count={selected.size}
        meId={me?.user_id}
        busy={bulkBusy}
        error={bulkError}
        onClose={() => {
          setBulkAction(null);
          setBulkError(null);
        }}
        onSubmit={runBulk}
      />
    </Stack>
  );
}

export function TasksHubPage() {
  usePageBreadcrumbs([{ label: "HUDDL HQ", to: "/app" }, { label: "Task Center" }]);
  return <TaskCenter />;
}

export function TasksQueuePage() {
  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Task Center", to: "/app/tasks" },
    { label: "Queue" },
  ]);
  return <TaskCenter />;
}
